import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import {
  ArrowLeft, MessageCircle, ShoppingBag, CheckCircle2, XCircle, Loader2,
  Package, Sparkles, Clock, ShieldAlert, Zap, X,
} from "lucide-react";
import {
  IntentBadge, StatusBadge, SOURCE_LABEL, BU_LABEL, fullDateTime, timeHM,
} from "../../components/OpportunityBadges";

const TIMELINE_LABEL = {
  OPPORTUNITY_CREATED: "Oportunidade criada",
  OPPORTUNITY_DISPATCH: "Revendedora notificada",
  OPPORTUNITY_VIEWED: "Visualizada",
  OPPORTUNITY_CONTACT_STARTED: "Atendimento iniciado",
  OPPORTUNITY_CONVERTED: "Convertida",
  OPPORTUNITY_LOST: "Não convertida",
  OPPORTUNITY_CANCELLED: "Cancelada",
};
const LOST_REASONS = [
  "Cliente desistiu", "Sem estoque", "Preço", "Não respondeu",
  "Comprará posteriormente", "Outro",
];

function Modal({ open, title, icon: Icon, children, onClose, testid }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9998] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div data-testid={testid} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl">
        <div className="flex items-start gap-3">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand-grad text-white">
            {Icon ? <Icon size={20} /> : null}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-brand-dark">{title}</h3>
            <div className="mt-2 text-sm text-[#6B5A69]">{children}</div>
          </div>
          <button onClick={onClose} aria-label="Fechar" data-testid={`${testid}-close`}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[#8A7A88] hover:bg-muted">
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OpportunityDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [opp, setOpp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errStatus, setErrStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [buChoiceOpen, setBuChoiceOpen] = useState(false);
  const [lostReason, setLostReason] = useState(LOST_REASONS[0]);
  const [lostNote, setLostNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrStatus(null);
    try {
      const r = await api.get(`/opportunities/${id}`);
      setOpp(r.data);
      // marca VIEWED apenas ao abrir de fato uma oportunidade NEW
      if (r.data?.status === "NEW") {
        try {
          const v = await api.post(`/opportunities/${id}/view`);
          setOpp((prev) => ({ ...prev, ...v.data, messages: prev?.messages, timeline: prev?.timeline }));
        } catch { /* nao bloqueia a leitura */ }
      }
    } catch (e) {
      setErrStatus(e?.response?.status || 500);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    try { const r = await api.get(`/opportunities/${id}`); setOpp(r.data); } catch { /* ignore */ }
  };

  const startContact = async () => {
    // VIEWED/NEW -> CONTACTING (backend e a autoridade; evita duplicar quando ja em atendimento)
    if (opp && ["NEW", "VIEWED"].includes(opp.status)) {
      try { await api.post(`/opportunities/${id}/contact`); } catch { /* ignore */ }
    }
  };

  const openWhatsApp = async () => {
    const phone = (opp?.customer_phone || "").replace(/\D/g, "");
    if (!phone) { toast.error("Cliente sem WhatsApp cadastrado"); return; }
    const first = (user?.name || "").split(" ")[0] || "sua revendedora";
    const clientFirst = (opp?.customer_name || "").split(" ")[0] || "";
    const interest = (opp?.product_text || "").trim();
    let msg = `Oi ${clientFirst}! Aqui é a ${first}, sua revendedora Cris Lingerie 💜\n\n`;
    msg += interest
      ? `Vi que você se interessou por "${interest}" no nosso WhatsApp. Posso te ajudar? 😊`
      : `Vi que você se interessou por um produto no nosso WhatsApp. Posso te ajudar? 😊`;
    await startContact();
    await refresh();
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  const goCreateSale = (unit) => {
    const cid = opp?.customer_id ? `client=${encodeURIComponent(opp.customer_id)}` : "";
    const interesse = opp?.product_text ? `interesse=${encodeURIComponent(opp.product_text)}` : "";
    const oid = `opportunity_id=${encodeURIComponent(opp.id)}`;
    const qs = [cid, interesse, oid].filter(Boolean).join("&");
    if (unit === "ELETROS") {
      const eqs = [cid, oid].filter(Boolean).join("&");
      nav(`/revenda/eletros/venda${eqs ? `?${eqs}` : ""}`);
    } else {
      nav(`/revenda/venda/nova${qs ? `?${qs}` : ""}`);
    }
  };

  const onCreateSale = () => {
    const bu = opp?.business_unit;
    if (bu === "ELETROS") return goCreateSale("ELETROS");
    if (bu === "CRIS") return goCreateSale("CRIS");
    setBuChoiceOpen(true); // UNKNOWN: nao escolher unidade silenciosamente
  };

  const doConvert = async () => {
    setBusy(true);
    try {
      await api.post(`/opportunities/${id}/convert`, {});
      toast.success("Marcada como venda realizada");
      setConvertOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível converter");
    } finally { setBusy(false); }
  };

  const doLost = async () => {
    setBusy(true);
    try {
      const reason = lostNote.trim() ? `${lostReason} — ${lostNote.trim()}` : lostReason;
      await api.post(`/opportunities/${id}/lost`, { reason });
      toast.success("Marcada como não convertida");
      setLostOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível registrar");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-1 py-6" data-testid="opp-detail-loading">
        <div className="h-8 w-40 animate-pulse rounded bg-muted" />
        <div className="mt-4 h-40 animate-pulse rounded-2xl bg-muted" />
        <div className="mt-3 h-40 animate-pulse rounded-2xl bg-muted" />
      </div>
    );
  }

  if (errStatus) {
    const denied = errStatus === 403 || errStatus === 404;
    return (
      <div className="mx-auto max-w-3xl px-1 py-10 text-center" data-testid="opp-detail-error">
        <ShieldAlert className="mx-auto text-red-500" size={32} />
        <p className="mt-3 text-base font-semibold text-brand-dark">
          {denied ? "Você não tem acesso a esta oportunidade." : "Erro ao carregar a oportunidade."}
        </p>
        <button onClick={() => nav("/revenda/oportunidades")} data-testid="opp-detail-back-err"
          className="mt-4 rounded-xl bg-brand-grad px-4 py-2 text-sm font-semibold text-white">
          Voltar para oportunidades
        </button>
      </div>
    );
  }

  const bu = BU_LABEL[opp.business_unit] || opp.business_unit;
  const isTerminal = ["CONVERTED", "LOST", "CANCELLED"].includes(opp.status);
  const messages = (opp.messages || []).filter((m) => m.message_text);
  const timeline = (opp.timeline || []).filter((t) => TIMELINE_LABEL[t.action]);

  return (
    <div className="mx-auto max-w-3xl px-1 pb-[13rem] sm:px-0 lg:pb-28" data-testid="opp-detail-page">
      <button onClick={() => nav("/revenda/oportunidades")} data-testid="opp-detail-back"
        className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline">
        <ArrowLeft size={16} /> Oportunidades
      </button>

      {/* Cabecalho */}
      <div className="rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="opp-detail-header">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xl font-bold text-brand-dark">{opp.customer_name || "Cliente sem nome"}</div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <IntentBadge level={opp.intent_level} />
              <StatusBadge status={opp.status} />
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-[#8A7A88]">Origem</div>
            <div className="font-medium text-brand-dark">{SOURCE_LABEL[opp.source] || opp.source}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-[#8A7A88]">Segmento</div>
            <div className="font-medium text-brand-dark">{bu}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-[#8A7A88]">Recebida em</div>
            <div className="font-medium text-brand-dark">{fullDateTime(opp.created_at)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-[#8A7A88]">Última mensagem</div>
            <div className="font-medium text-brand-dark">{fullDateTime(opp.last_message_at)}</div>
          </div>
        </div>
      </div>

      {/* Conversão vinculada a venda (OP-7) */}
      {opp.status === "CONVERTED" && opp.converted_sale_id && (
        <div className="mt-3 flex items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-card" data-testid="opp-converted-banner">
          <div className="text-sm">
            <div className="font-bold text-emerald-800">✅ Venda realizada</div>
            <div className="text-emerald-700">{opp.conversion_source === "SALE_LINK" ? "Convertida automaticamente pela venda vinculada." : "Conversão manual."}</div>
          </div>
          {opp.conversion_source === "SALE_LINK" && (
            <button onClick={() => nav(opp.business_unit === "ELETROS" ? "/revenda/eletros" : `/vendas/${opp.converted_sale_id}`)}
              data-testid="opp-view-sale-btn" className="shrink-0 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white">
              Ver venda
            </button>
          )}
        </div>
      )}

      {/* Interesse */}
      <div className="mt-3 rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="opp-detail-interest">
        <div className="flex items-center gap-2 text-sm font-bold text-brand-dark">
          <Package size={16} className="text-brand" /> Interesse identificado
        </div>
        <p className="mt-2 text-sm text-[#4A3A48]">
          {(opp.product_text || "Interesse não detalhado na mensagem.").trim()}
        </p>
        {opp.product_id ? (
          <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
            <Sparkles size={12} /> Produto do catálogo vinculado
          </div>
        ) : null}
      </div>

      {/* Mensagem original + historico da sessao */}
      {opp.original_message ? (
        <div className="mt-3 rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="opp-detail-original">
          <div className="text-sm font-bold text-brand-dark">Mensagem recebida</div>
          <blockquote className="mt-2 rounded-xl bg-muted px-3 py-2 text-sm italic text-[#4A3A48]">
            “{opp.original_message}”
          </blockquote>
        </div>
      ) : null}

      {messages.length > 0 && (
        <div className="mt-3 rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="opp-detail-messages">
          <div className="text-sm font-bold text-brand-dark">Histórico da sessão</div>
          <ul className="mt-2 space-y-1.5">
            {messages.map((m) => (
              <li key={m.id} className="flex gap-2 text-sm">
                <span className="shrink-0 tabular-nums text-[#8A7A88]">{timeHM(m.created_at)}</span>
                <span className={m.direction === "out" ? "text-brand" : "text-[#4A3A48]"}>
                  {m.direction === "out" ? "→ " : ""}{m.message_text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Timeline */}
      {timeline.length > 0 && (
        <div className="mt-3 rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="opp-detail-timeline">
          <div className="flex items-center gap-2 text-sm font-bold text-brand-dark">
            <Clock size={16} className="text-brand" /> Linha do tempo
          </div>
          <ol className="mt-3 space-y-3">
            {timeline.map((t, i) => (
              <li key={t.id || i} className="flex gap-3">
                <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand" />
                <div>
                  <div className="text-sm font-medium text-brand-dark">{TIMELINE_LABEL[t.action]}</div>
                  <div className="text-[11px] text-[#8A7A88]">{fullDateTime(t.at)}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Acoes fixas */}
      <div className="fixed inset-x-0 bottom-[calc(64px+env(safe-area-inset-bottom))] z-30 border-t border-border bg-white/95 p-3 backdrop-blur lg:bottom-0 lg:left-72" data-testid="opp-detail-actions">
        <div className="mx-auto flex max-w-3xl flex-col gap-2">
          <button onClick={openWhatsApp} data-testid="opp-whatsapp-btn"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-sm font-bold text-white hover:bg-emerald-600">
            <MessageCircle size={18} /> Falar com cliente no WhatsApp
          </button>
          <div className="grid grid-cols-3 gap-2">
            <button onClick={onCreateSale} disabled={isTerminal} data-testid="opp-create-sale-btn"
              className="flex items-center justify-center gap-1.5 rounded-xl bg-brand-grad py-2.5 text-xs font-bold text-white disabled:opacity-40">
              <ShoppingBag size={15} /> Criar venda
            </button>
            <button onClick={() => setConvertOpen(true)} disabled={isTerminal} data-testid="opp-convert-btn"
              className="flex items-center justify-center gap-1.5 rounded-xl bg-emerald-100 py-2.5 text-xs font-bold text-emerald-700 disabled:opacity-40">
              <CheckCircle2 size={15} /> Venda realizada
            </button>
            <button onClick={() => setLostOpen(true)} disabled={isTerminal} data-testid="opp-lost-btn"
              className="flex items-center justify-center gap-1.5 rounded-xl bg-red-100 py-2.5 text-xs font-bold text-red-700 disabled:opacity-40">
              <XCircle size={15} /> Não convertida
            </button>
          </div>
        </div>
      </div>

      {/* Modal converter */}
      <Modal open={convertOpen} title="Marcar como venda realizada" icon={CheckCircle2} onClose={() => setConvertOpen(false)} testid="opp-convert-modal">
        <p>Confirme que esta oportunidade virou uma venda. O vínculo é manual (a venda não é criada automaticamente nesta etapa).</p>
        <div className="mt-5 flex gap-3">
          <button onClick={() => setConvertOpen(false)} disabled={busy} data-testid="opp-convert-cancel"
            className="flex-1 rounded-xl border border-border bg-white py-2.5 text-sm font-semibold text-brand-dark hover:bg-muted">Voltar</button>
          <button onClick={doConvert} disabled={busy} data-testid="opp-convert-confirm"
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-500 py-2.5 text-sm font-semibold text-white">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null} Confirmar
          </button>
        </div>
      </Modal>

      {/* Modal nao convertida */}
      <Modal open={lostOpen} title="Marcar como não convertida" icon={XCircle} onClose={() => setLostOpen(false)} testid="opp-lost-modal">
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#8A7A88]">Motivo</label>
        <select value={lostReason} onChange={(e) => setLostReason(e.target.value)} data-testid="opp-lost-reason"
          className="w-full rounded-xl border border-border bg-white px-3 py-2.5 text-sm text-brand-dark focus:border-brand focus:outline-none">
          {LOST_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <textarea value={lostNote} onChange={(e) => setLostNote(e.target.value)} rows={2} placeholder="Observação (opcional)"
          data-testid="opp-lost-note"
          className="mt-2 w-full rounded-xl border border-border bg-white px-3 py-2 text-sm text-brand-dark focus:border-brand focus:outline-none" />
        <div className="mt-5 flex gap-3">
          <button onClick={() => setLostOpen(false)} disabled={busy} data-testid="opp-lost-cancel"
            className="flex-1 rounded-xl border border-border bg-white py-2.5 text-sm font-semibold text-brand-dark hover:bg-muted">Voltar</button>
          <button onClick={doLost} disabled={busy} data-testid="opp-lost-confirm"
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-500 py-2.5 text-sm font-semibold text-white">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null} Confirmar
          </button>
        </div>
      </Modal>

      {/* Modal escolha de unidade (UNKNOWN) */}
      <Modal open={buChoiceOpen} title="Qual venda deseja criar?" icon={ShoppingBag} onClose={() => setBuChoiceOpen(false)} testid="opp-bu-choice-modal">
        <p>O segmento desta oportunidade não foi identificado. Escolha onde registrar a venda:</p>
        <div className="mt-5 grid grid-cols-1 gap-2">
          <button onClick={() => { setBuChoiceOpen(false); goCreateSale("CRIS"); }} data-testid="opp-bu-choice-cris"
            className="flex items-center justify-center gap-2 rounded-xl bg-brand-grad py-2.5 text-sm font-semibold text-white">
            <ShoppingBag size={16} /> Venda Cris Lingerie
          </button>
          <button onClick={() => { setBuChoiceOpen(false); goCreateSale("ELETROS"); }} data-testid="opp-bu-choice-eletros"
            className="flex items-center justify-center gap-2 rounded-xl border border-border bg-white py-2.5 text-sm font-semibold text-[#C9960A]">
            <Zap size={16} /> Venda Eletros
          </button>
        </div>
      </Modal>
    </div>
  );
}
